/**
 * SNES Chat - State Management
 *
 * Simple state management for the application.
 * Based on data-model.md specification.
 */

import type {
  AppState,
  ChatMessage,
  GateState,
  MCPConnectionState,
  StateChangeCallback,
} from './types';

// ============================================================================
// Constants
// ============================================================================

const GATE_STORAGE_KEY = 'maint-tracka-gate-passed';
const GATE_TIMESTAMP_KEY = 'maint-tracka-gate-timestamp';

// ============================================================================
// Initial State
// ============================================================================

/**
 * Check localStorage for existing gate status
 */
function checkGateFromStorage(): GateState {
  try {
    const passed = localStorage.getItem(GATE_STORAGE_KEY) === 'true';
    const timestamp = localStorage.getItem(GATE_TIMESTAMP_KEY);

    return {
      passed,
      timestamp: timestamp ? parseInt(timestamp, 10) : null,
    };
  } catch {
    // localStorage not available
    return { passed: false, timestamp: null };
  }
}

/**
 * Create initial application state
 */
function createInitialState(): AppState {
  return {
    gate: checkGateFromStorage(),
    messages: [],
    inputValue: '',
    isLoading: false,
    mcp: {
      status: 'disconnected',
      sessionId: null,
      lastError: null,
      availableTools: [],
    },
  };
}

// ============================================================================
// State Store
// ============================================================================

let currentState: AppState = createInitialState();
const listeners: Set<StateChangeCallback> = new Set();

/**
 * Get current application state
 */
export function getState(): AppState {
  return currentState;
}

/**
 * Update state and notify listeners
 */
function setState(newState: Partial<AppState>): void {
  currentState = { ...currentState, ...newState };
  notifyListeners();
}

/**
 * Subscribe to state changes
 */
export function subscribe(callback: StateChangeCallback): () => void {
  listeners.add(callback);
  return () => listeners.delete(callback);
}

/**
 * Notify all listeners of state change
 */
function notifyListeners(): void {
  listeners.forEach((callback) => callback(currentState));
}

// ============================================================================
// Gate State Actions
// ============================================================================

/**
 * Set gate as passed and persist to localStorage
 */
export function setGatePassed(): void {
  const timestamp = Math.floor(Date.now() / 1000);

  try {
    localStorage.setItem(GATE_STORAGE_KEY, 'true');
    localStorage.setItem(GATE_TIMESTAMP_KEY, timestamp.toString());
  } catch {
    // localStorage not available, continue with in-memory only
  }

  setState({
    gate: { passed: true, timestamp },
  });
}

/**
 * Clear gate status (for testing/reset)
 */
export function clearGateStatus(): void {
  try {
    localStorage.removeItem(GATE_STORAGE_KEY);
    localStorage.removeItem(GATE_TIMESTAMP_KEY);
  } catch {
    // localStorage not available
  }

  setState({
    gate: { passed: false, timestamp: null },
  });
}

/**
 * Check if gate is passed
 */
export function isGatePassed(): boolean {
  return currentState.gate.passed;
}

// ============================================================================
// Chat State Actions
// ============================================================================

/**
 * Add a message to the chat
 */
export function addMessage(message: ChatMessage): void {
  setState({
    messages: [...currentState.messages, message],
  });
}

/**
 * Update a message by ID
 */
export function updateMessage(id: string, updates: Partial<ChatMessage>): void {
  setState({
    messages: currentState.messages.map((msg) =>
      msg.id === id ? { ...msg, ...updates } : msg
    ),
  });
}

/**
 * Clear all messages
 */
export function clearMessages(): void {
  setState({ messages: [] });
}

/**
 * Set input value
 */
export function setInputValue(value: string): void {
  setState({ inputValue: value });
}

/**
 * Set loading state
 */
export function setLoading(isLoading: boolean): void {
  setState({ isLoading });
}

// ============================================================================
// MCP Connection State Actions
// ============================================================================

/**
 * Update MCP connection state
 */
export function updateMCPState(updates: Partial<MCPConnectionState>): void {
  setState({
    mcp: { ...currentState.mcp, ...updates },
  });
}

/**
 * Set MCP as connected
 */
export function setMCPConnected(sessionId: string): void {
  updateMCPState({
    status: 'connected',
    sessionId,
    lastError: null,
  });
}

/**
 * Set MCP as disconnected
 */
export function setMCPDisconnected(): void {
  updateMCPState({
    status: 'disconnected',
    sessionId: null,
  });
}

/**
 * Set MCP error
 */
export function setMCPError(error: string): void {
  updateMCPState({
    status: 'error',
    lastError: error,
  });
}

/**
 * Set MCP as connecting
 */
export function setMCPConnecting(): void {
  updateMCPState({
    status: 'connecting',
    lastError: null,
  });
}

// ============================================================================
// Reset
// ============================================================================

/**
 * Reset entire state to initial values
 */
export function resetState(): void {
  currentState = createInitialState();
  notifyListeners();
}
